/**
 * Candidate Pipeline — 7-lane kanban with:
 *   • native HTML5 drag-and-drop between lanes
 *   • lane-label pill on every candidate card so the current stage is
 *     ALWAYS visible (not just via column position)
 *   • quick-move buttons for keyboard/mobile users (all 6 other lanes shown)
 *   • per-card avatar + LinkedIn logo-only link
 *   • click on the card body opens the CandidateDrawer
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import { Linkedin, ExternalLink, GripVertical, Mail, Phone, Sparkles, MapPin, ArrowRight } from "lucide-react";
import CandidateAvatar from "@/components/CandidateAvatar";
import { openChat } from "@/lib/chatBus";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const STAGE_IDS = ["sourced", "contacted", "interested", "screening", "submitted", "placed", "declined"];
const STAGE_COLORS = {
    sourced:    { color: "#94a3b8", tint: "rgba(148,163,184,0.12)" },
    contacted:  { color: "#3b82f6", tint: "rgba(59,130,246,0.12)" },
    interested: { color: "#f59e0b", tint: "rgba(245,158,11,0.14)" },
    screening:  { color: "#ff5a00", tint: "rgba(255,90,0,0.14)" },
    submitted:  { color: "#a855f7", tint: "rgba(168,85,247,0.14)" },
    placed:     { color: "#22c55e", tint: "rgba(34,197,94,0.14)" },
    declined:   { color: "#ef4444", tint: "rgba(239,68,68,0.12)" },
};

export default function CandidatePipeline() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const STAGES = STAGE_IDS.map((id) => ({ id, label: t(`pipeline_recruit.stage_${id}`), ...STAGE_COLORS[id] }));
    const [cands, setCands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dragId, setDragId] = useState(null);
    const [dragOver, setDragOver] = useState(null);

    const load = () => api.get("/candidates").then((r) => { setCands(r.data); setLoading(false); });
    useEffect(() => { load(); }, []);

    // Drill-down vanaf het dashboard: ?stage=x → scroll naar de lane + highlight
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const stage = searchParams.get("stage");
        if (loading || !stage) return;
        const el = document.querySelector(`[data-testid="recruit-stage-${stage}"]`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
            el.classList.add("stage-highlight");
            const t = setTimeout(() => el.classList.remove("stage-highlight"), 3000);
            return () => clearTimeout(t);
        }
    }, [loading]);

    const move = async (id, stage, silent = false) => {
        // Optimistic update — instant UI feedback for DnD
        setCands((prev) => prev.map((c) => c.id === id ? { ...c, stage } : c));
        try {
            await api.patch(`/candidates/${id}`, { stage });
            if (!silent) toast.success(`→ ${t(`pipeline_recruit.stage_${stage}`)}`);
        } catch (e) {
            toast.error(i18n.t("companies.move_failed"));
            load();
        }
    };

    const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
    const onDragOver = (e, stage) => { e.preventDefault(); setDragOver(stage); };
    const onDrop = (e, stage) => {
        e.preventDefault();
        setDragOver(null);
        if (dragId) {
            const cur = cands.find((c) => c.id === dragId);
            if (cur && cur.stage !== stage) move(dragId, stage);
        }
        setDragId(null);
    };

    return (
        <PageHeader title={t("pipeline_recruit.title")} subtitle={t("pipeline_recruit.subtitle")}>
            {loading ? <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div> : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2 overflow-x-auto pb-4" data-testid="kanban-grid">
                    {STAGES.map((st) => {
                        const items = cands.filter((c) => c.stage === st.id);
                        const isOver = dragOver === st.id;
                        return (
                            <div key={st.id} data-testid={`recruit-stage-${st.id}`}
                                onDragOver={(e) => onDragOver(e, st.id)}
                                onDragLeave={() => setDragOver(null)}
                                onDrop={(e) => onDrop(e, st.id)}
                                style={{ background: isOver ? st.tint : undefined, borderColor: isOver ? st.color : undefined }}
                                className={`rounded-xl border ${isOver ? "border-2" : "border-white/8"} bg-[var(--jr-surface)] flex flex-col min-h-[520px] transition-colors`}>
                                <div className="px-3 py-3 border-b border-white/8 flex items-center justify-between" style={{ borderBottomColor: `${st.color}30` }}>
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: st.color }}/>
                                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] truncate" style={{ color: st.color }}>{st.label}</span>
                                    </div>
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${st.color}22`, color: st.color }}>{items.length}</span>
                                </div>
                                <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                                    {items.slice(0, 30).map((c) => (
                                        <KanbanCard key={c.id} c={c} stage={st}
                                            onDragStart={onDragStart} onOpen={() => nav(`/app/recruit/candidate/${c.id}`)} onMove={move} t={t} />
                                    ))}
                                    {items.length === 0 && (
                                        <div className="text-center text-[10px] text-[var(--jr-text-3)] py-4 opacity-60">
                                            {t("pipeline_recruit.drop_here")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </PageHeader>
    );
}

function KanbanCard({ c, stage, onDragStart, onOpen, onMove, t }) {
    const navigate = useNavigate();
    const otherStages = STAGE_IDS.filter((sid) => sid !== stage.id).map((sid) => ({
        id: sid, label: t(`pipeline_recruit.stage_${sid}`), ...STAGE_COLORS[sid],
    }));
    const nativeTip = [c.name, c.title].filter(Boolean).join(" · ");
    const skills = (c.skills || []).slice(0, 6);

    const askBriefing = (ev) => {
        ev.stopPropagation();
        const prompt = i18n.t("candpage.briefing_prompt", { name: c.name, title: c.title ? ` (${c.title})` : "", company: c.current_company ? ` @ ${c.current_company}` : "" });
        openChat({ masterId: "master_recruiter_agent", context: `candidate:${c.id}`, prompt });
    };
    const openProfile = (ev) => {
        ev.stopPropagation();
        navigate(`/app/recruit/candidate/${c.id}`);
    };
    return (
        <HoverCard openDelay={220} closeDelay={80}>
            <HoverCardTrigger asChild>
        <div draggable
            onDragStart={(e) => onDragStart(e, c.id)}
            data-testid={`recruit-card-${c.id}`}
            title={nativeTip}
            className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-2.5 cursor-grab active:cursor-grabbing hover:border-white/25 hover:shadow-[0_6px_20px_-6px_rgba(0,0,0,0.35)] transition-all">
            {/* Header row: drag handle + avatar + name */}
            <div className="flex items-start gap-2">
                <GripVertical size={11} className="text-[var(--jr-text-3)] mt-0.5 shrink-0"/>
                <CandidateAvatar name={c.name} url={c.photo_url} size={28} />
                <button type="button" onClick={onOpen} data-testid={`recruit-open-${c.id}`}
                    className="text-left flex-1 min-w-0 hover:opacity-90">
                    <div className="text-xs font-medium text-white truncate" title={c.name}>{c.name}</div>
                    <div className="text-[10px] text-[var(--jr-text-2)] truncate" title={c.title}>{c.title}</div>
                </button>
            </div>

            {/* Current stage label — always visible so context is clear */}
            <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                <span className="text-[8px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded" style={{ background: `${stage.color}22`, color: stage.color }}>
                    {stage.label}
                </span>
                <LinkedInButton url={c.linkedin_url} name={c.name} variant="icon" className="!w-5 !h-5" testid={`kanban-linkedin-${c.id}`} resolve={{ type: "candidate", id: c.id }} />
            </div>

            {/* Quick-move buttons — ALL 6 other lanes visible, colour-coded per lane */}
            <div className="mt-2 pt-2 border-t border-white/8">
                <div className="text-[8px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] mb-1">{t("pipeline_recruit.move_to")}</div>
                <div className="grid grid-cols-3 gap-1">
                    {otherStages.map((s) => (
                        <button key={s.id} onClick={(e) => { e.stopPropagation(); onMove(c.id, s.id); }}
                            data-testid={`move-${c.id}-${s.id}`}
                            title={s.label}
                            style={{ color: s.color, borderColor: `${s.color}55` }}
                            className="text-[9px] font-mono uppercase tracking-[0.10em] px-1 py-0.5 rounded border hover:bg-white/5 truncate">
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
            </HoverCardTrigger>
            <HoverCardContent side="right" align="start" sideOffset={10}
                className="w-80 z-50 rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4 text-white shadow-2xl"
                data-testid={`candidate-hover-${c.id}`}>
                <div className="flex items-start gap-3">
                    <CandidateAvatar name={c.name} url={c.photo_url} size={38} />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold leading-snug break-words">{c.name}</div>
                        {c.title && <div className="text-xs text-[var(--jr-text-2)] mt-0.5 break-words">{c.title}</div>}
                    </div>
                    <span className="text-[8px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${stage.color}22`, color: stage.color }}>
                        {stage.label}
                    </span>
                </div>
                {(c.current_company || c.location) && (
                    <div className="mt-2 text-xs text-[var(--jr-text-2)] flex items-center gap-1.5 flex-wrap">
                        {c.current_company && <span>{c.current_company}</span>}
                        {c.current_company && c.location && <span className="text-[var(--jr-text-3)]">·</span>}
                        {c.location && <span className="inline-flex items-center gap-1 text-[var(--jr-text-3)]"><MapPin size={10}/> {c.location}</span>}
                    </div>
                )}
                {skills.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                        {skills.map((s, i) => (
                            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                        ))}
                    </div>
                )}
                {(c.email || c.phone) && (
                    <div className="mt-3 space-y-0.5 text-xs">
                        {c.email && <div className="text-[var(--jr-text-2)] truncate"><Mail size={10} className="inline mr-1.5 text-[var(--jr-text-3)]"/>{c.email}</div>}
                        {c.phone && <div className="text-[var(--jr-text-2)]"><Phone size={10} className="inline mr-1.5 text-[var(--jr-text-3)]"/>{c.phone}</div>}
                    </div>
                )}
                {c.notes && (
                    <div className="mt-3 rounded-md bg-white/[0.04] border border-white/6 p-2 text-[11px] text-[var(--jr-text-2)] leading-relaxed line-clamp-3">
                        <Sparkles size={10} className="inline mr-1 text-[var(--jr-brand)]"/> {c.notes}
                    </div>
                )}
                {/* Quick-actions row */}
                <div className="mt-3 pt-3 border-t border-white/6 flex items-center gap-2">
                    <button onClick={openProfile}
                        data-testid={`hover-open-profile-${c.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[var(--jr-brand)]/12 border border-[var(--jr-brand)]/30 text-[11px] font-medium text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/20 transition-colors">
                        <ArrowRight size={11}/>
                        Open profiel
                    </button>
                    <button onClick={askBriefing}
                        data-testid={`hover-briefing-${c.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/10 text-[11px] font-medium text-[var(--jr-text-2)] hover:text-white hover:bg-white/[0.08] transition-colors">
                        <Sparkles size={11}/>
                        AI briefing
                    </button>
                </div>
                <div className="mt-2 text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                    Sleep card → verplaats stage
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}
